/**
 * @author Mevan Nirosh
 * @version 0.1
 * @since 2/13/2025
 */
import React, { useEffect, useState, useMemo } from "react"
import api from "../../../../base/services/ResApi"
import { MaterialReactTable } from "material-react-table"
import "../../../../styleSheets/base/styles.scss"
import "../styles/terminals.styles.scss"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import Alerts from "../../../../base/errorHandeling/Alerts"
import SearchIcon from "@mui/icons-material/Search"
import ClearIcon from "@mui/icons-material/Clear"

const TerminalHistoryModal = ({ open, onClose }) => {
  const [transformedData, setTransformedData] = useState([])
  const [appRowCount, setAppRowCount] = useState(0)
  const [approvedData, setApprovedData] = useState([])
  const [apprLoading, setApprDataLoading] = useState(false)

  const [terminalSerial, setTerminalSerial] = useState("")
  const [search, setSearch] = useState(null)

  const [iserrorShow, setIserrorShow] = useState(false)
  const [iserror, setIserror] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])


  //table state
  const [columnFilters, setColumnFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const columns = useMemo(
    () => [
      {
        accessorKey: "orgName",
        header: "Org",
      },
      {
        accessorKey: "grpName",
        header: "Group",
      },
      {
        accessorKey: "deployStgy",
        header: "Deploy Strategy",
      },
      {
        accessorKey: "deployStatus",
        header: "Deploy Status",
      },

      {
        accessorKey: "appUploadedBy",
        header: "App uploaded by",
      },
      {
        accessorKey: "appUploadedDateTime",
        header: "App uploaded date",
      },
      {
        accessorKey: "lastUpdatedBy",
        header: "Last updated by",
      },
      {
        accessorKey: "lastUpdatedDateTime",
        header: "Last updated date",
      },
      {
        accessorKey: "tmnlAppStatus",
        header: "Terminal App Status",
      },

      {
        accessorKey: "appId",
        header: "App ID",
      },
      {
        accessorKey: "appName",
        header: "Name",
      },
      {
        accessorKey: "appVersion",
        header: "Version",
      },
      {
        accessorKey: "versionCode",
        header: "Version Code",
      },
      {
        accessorKey: "uploadOn",
        header: "Upload Date",
      },
      {
        accessorKey: "histCreatedDateTime",
        header: "His. Created on",
      },
    ],
    []
  )

  useEffect(() => {
    if (!open) setSearch(null)
  }, [open])

  
  useEffect(() => {
    if (!open || !search) {
      setApprDataLoading(false)
      return
    }
    let active = true
    const apprvConfig = {
      headers: {
        Action: "V_APP_VER_HIST",
        "Is-Pagination": true,
        "Page-Number": pagination.pageIndex + 1,
        "Page-Size": pagination.pageSize,
        "Sort-Column": "version_code",
        "Sort-Order": "desc",
        Filter: "F_TRMNL_SERIAL",
        Value: search.serial,
      },
    }

    setApprDataLoading(true)
    setIserror(false)
    setIserrorShow(false)
    api
      .get("/applications", apprvConfig)
      .then((res) => {
        if (!active) return
        setApprovedData(res.data?.dataList ?? [])
        setAppRowCount(res.data?.total ?? 0)
      })
      .catch((error) => {
        if (!active) return
        setApprovedData([])
        setAppRowCount(0)
        setErrorMessages([error.response?.data?.description || "Unable to load terminal history. Please try again."])
        setIserror(true)
        setIserrorShow(true)
      })
      .finally(() => { if (active) setApprDataLoading(false) })

    return () => { active = false }
  }, [open, search, pagination.pageIndex, pagination.pageSize])

  useEffect(() => {
    const transformedData = approvedData.map((item) => {
      const { orgGrpDetail, tmnlAppBroadcastDtl, ...rest } =
        item

      return {
        ...rest,
        deployStatus: orgGrpDetail?.deployStatus,
        deployStgy: orgGrpDetail?.deployStgy,
        grpName: orgGrpDetail?.grpName,
        orgName: orgGrpDetail?.orgName,
        appUploadedBy: tmnlAppBroadcastDtl?.appUploadedBy,
        appUploadedDateTime: tmnlAppBroadcastDtl?.appUploadedDateTime,
        lastUpdatedBy: tmnlAppBroadcastDtl?.lastUpdatedBy,
        lastUpdatedDateTime: tmnlAppBroadcastDtl?.lastUpdatedDateTime,
        tmnlAppStatus: tmnlAppBroadcastDtl?.tmnlAppStatus,
      }
    })

    setTransformedData(transformedData)
  }, [approvedData])

  const handleTerminalSerialChange = (e) => {
    const value = e.target.value
    setTerminalSerial(value)
  }
  const clearFilters = () => {
    setTerminalSerial("")
    setSearch(null)
    setApprovedData([])
    setAppRowCount(0)
    setPagination((previous) => ({ ...previous, pageIndex: 0 }))
    setIserror(false)
    setIserrorShow(false)
  }
  const handleSearch = () => {
    const serial = terminalSerial.trim()
    if (!serial) return
    setApprovedData([])
    setAppRowCount(0)
    setPagination((previous) => ({ ...previous, pageIndex: 0 }))
    setSearch({ serial })
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="lg"
      open={open}
      PaperProps={{
        style: { borderRadius: "10px", width: "1500px", margin: 0 },
      }}
    >
      <DialogTitle className="terminalAppHistory">
        View Terminal App History
        <IconButton aria-label="cancel" onClick={() => onClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <div>
          <Alerts
            iserror={iserror}
            errorMessages={errorMessages}
            iserrorShow={iserrorShow}
          />
        </div>
        <Stack spacing={2} direction="row" sx={{ marginBottom: 2 }}>
          <TextField
            name="terminalSerial"
            value={terminalSerial}
            type="text"
            variant="outlined"
            color="secondary"
            label="Terminal Serial"
            onChange={handleTerminalSerialChange}
          />
          <Button
            variant="contained"
            color="warning"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={!terminalSerial.trim() || apprLoading}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
          >
            Clear
          </Button>
        </Stack>
        <MaterialReactTable
          columns={columns}
          data={transformedData ?? []}
          state={{
            columnFilters,
            globalFilter,
            pagination,
            showProgressBars: apprLoading,
          }}
          initialState={{
            showColumnFilters: false,
            columnVisibility: {
              appId: false,
              versionCode: false,
              createdBy: false,
            },
          }}
          manualPagination
          onColumnFiltersChange={setColumnFilters}
          onGlobalFilterChange={setGlobalFilter}
          onPaginationChange={setPagination}
          rowCount={appRowCount}
          muiTablePaginationProps={{
            rowsPerPageOptions: [10],
          }}
          enableColumnOrdering
          enableGrouping
          enablePinning
          enableColumnResizing
          columnResizeMode="onEnd"
        />
      </DialogContent>
      <DialogActions sx={{ p: "1.25rem" }}>
        <Button onClick={() => onClose()} variant="outlined" color="error">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TerminalHistoryModal
